import React, { useCallback, useEffect, useState } from "react";
import { Card, Col, DatePicker, Row, Table } from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import ReportShell from "../ReportShell";
import {
  DateRange,
  DEFAULT_RANGE,
  RANGE_PRESETS,
  rangeLabel,
  rangeParams,
} from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  formatCurrency,
  pdfCurrency,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { RangePicker } = DatePicker;

interface Props {
  pharmacy: PharmacyHeader;
}

const GstReport: React.FC<Props> = ({ pharmacy }) => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getGstReport(rangeParams(range));
      setData(res.data.data);
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load GST report");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const outward = summary.outward || {};
  const inward = summary.inward || {};
  const monthly = data?.monthly || [];

  const exportColumns: ExportColumn[] = [
    {
      title: "Month",
      value: (r) => dayjs(`${r.month}-01`).format("MMM YYYY"),
    },
    { title: "Outward Taxable", value: (r) => (r.outward?.taxable || 0).toFixed(2) },
    { title: "Outward CGST", value: (r) => (r.outward?.cgst || 0).toFixed(2) },
    { title: "Outward SGST", value: (r) => (r.outward?.sgst || 0).toFixed(2) },
    { title: "Outward IGST", value: (r) => (r.outward?.igst || 0).toFixed(2) },
    { title: "Outward Total GST", value: (r) => (r.outward?.totalGst || 0).toFixed(2) },
    { title: "Inward Taxable", value: (r) => (r.inward?.taxable || 0).toFixed(2) },
    { title: "Inward CGST", value: (r) => (r.inward?.cgst || 0).toFixed(2) },
    { title: "Inward SGST", value: (r) => (r.inward?.sgst || 0).toFixed(2) },
    { title: "Inward IGST", value: (r) => (r.inward?.igst || 0).toFixed(2) },
    { title: "Inward Total GST", value: (r) => (r.inward?.totalGst || 0).toFixed(2) },
    {
      title: "Net GST",
      value: (r) =>
        ((r.outward?.totalGst || 0) - (r.inward?.totalGst || 0)).toFixed(2),
    },
  ];

  const summaryLines = () => [
    `Outward GST: ${(outward.totalGst || 0).toFixed(2)} | Inward GST: ${(inward.totalGst || 0).toFixed(2)} | Net payable: ${(summary.netGstPayable || 0).toFixed(2)}`,
  ];

  const handleExportCsv = () => {
    exportCsv("GST Summary Report", exportColumns, monthly, [
      `Period: ${rangeLabel(range)}`,
      ...summaryLines(),
    ]);
  };

  const handleExportPdf = () => {
    exportPdf({
      title: "GST Summary Report",
      pharmacy,
      subtitle: `Period: ${rangeLabel(range)}`,
      orientation: "landscape",
      columns: exportColumns,
      rows: monthly,
      summary: [
        { label: "Outward GST", value: pdfCurrency(outward.totalGst) },
        { label: "Inward GST", value: pdfCurrency(inward.totalGst) },
        { label: "Net GST payable", value: pdfCurrency(summary.netGstPayable) },
      ],
    });
  };

  const gstCard = (title: string, row: any) => (
    <Card title={title} size="small" loading={loading}>
      <Table
        size="small"
        pagination={false}
        showHeader={false}
        rowKey="label"
        dataSource={[
          { label: "Taxable", value: row.taxable },
          { label: "CGST", value: row.cgst },
          { label: "SGST", value: row.sgst },
          { label: "IGST", value: row.igst },
          { label: "Total GST", value: row.totalGst, strong: true },
        ]}
        columns={[
          {
            dataIndex: "label",
            render: (v: string, r: any) => (r.strong ? <strong>{v}</strong> : v),
          },
          {
            dataIndex: "value",
            align: "right" as const,
            render: (v: number, r: any) =>
              r.strong ? <strong>{formatCurrency(v)}</strong> : formatCurrency(v),
          },
        ]}
      />
    </Card>
  );

  return (
    <ReportShell
      title="GST Summary"
      loading={loading}
      error={error}
      onRefresh={load}
      onReset={() => setRange(DEFAULT_RANGE)}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <RangePicker
          size="small"
          value={range}
          allowClear={false}
          presets={RANGE_PRESETS}
          onChange={(v) => v && setRange(v as DateRange)}
        />
      }
      stats={[
        {
          title: "Outward GST (sales)",
          value: outward.totalGst || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Inward GST (purchases)",
          value: inward.totalGst || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Net GST payable",
          value: summary.netGstPayable || 0,
          prefix: "₹",
          precision: 2,
        },
      ]}
      extra={
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={24} md={12}>
            {gstCard(
              `Outward — Sales (${outward.docCount || 0} bills)`,
              outward
            )}
          </Col>
          <Col xs={24} md={12}>
            {gstCard(
              `Inward — Purchases (${inward.docCount || 0} invoices)`,
              inward
            )}
          </Col>
        </Row>
      }
    >
      <Table
        rowKey="month"
        size="small"
        sticky
        loading={loading}
        dataSource={monthly}
        scroll={{ x: 900 }}
        pagination={false}
        columns={[
          {
            title: "Month",
            dataIndex: "month",
            fixed: "left",
            width: 100,
            render: (m: string) => dayjs(`${m}-01`).format("MMM YYYY"),
          },
          {
            title: "Outward (Sales)",
            children: [
              {
                title: "Taxable",
                width: 110,
                align: "right" as const,
                render: (_: unknown, r: any) => formatCurrency(r.outward?.taxable),
              },
              {
                title: "GST",
                width: 100,
                align: "right" as const,
                render: (_: unknown, r: any) => formatCurrency(r.outward?.totalGst),
              },
            ],
          },
          {
            title: "Inward (Purchases)",
            children: [
              {
                title: "Taxable",
                width: 110,
                align: "right" as const,
                render: (_: unknown, r: any) => formatCurrency(r.inward?.taxable),
              },
              {
                title: "GST",
                width: 100,
                align: "right" as const,
                render: (_: unknown, r: any) => formatCurrency(r.inward?.totalGst),
              },
            ],
          },
          {
            title: "Net GST",
            width: 110,
            align: "right" as const,
            render: (_: unknown, r: any) =>
              formatCurrency(
                (r.outward?.totalGst || 0) - (r.inward?.totalGst || 0)
              ),
          },
        ]}
      />
    </ReportShell>
  );
};

export default GstReport;
